import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Check, ChartNoAxesColumnIncreasing } from "lucide-react";
import type { FinanceProgress } from "@chro/shared";
import "../../features/eex/finance-progress.css";

const stages: Record<FinanceProgress["stage"], { label: string; step: number }> = {
  sources: { label: "Loading trades and settlement data", step: 0 },
  recognition: { label: "Calculating recognized P&L", step: 1 },
  schedules: { label: "Applying recognition schedules", step: 1 },
  bilateral: { label: "Preparing physical and OTC context", step: 1 },
  baseline: { label: "Calculating opening balances", step: 1 },
  vm: { label: "Calculating variation margin", step: 2 },
  carry: { label: "Calculating monthly carry", step: 2 },
  assembling: { label: "Bringing your report together", step: 3 },
  transferring: { label: "Preparing your table", step: 3 },
};

/** Request-local progress must never leak into another company/range or a cached report. */
export function useFinanceProgress(scope: string) {
  const generation = useRef(0);
  const [state, setState] = useState<{ scope: string; value: FinanceProgress | null }>({ scope, value: null });
  useEffect(() => () => { generation.current++; }, []);
  const start = useCallback((signal: AbortSignal) => {
    const request = ++generation.current;
    setState({ scope, value: null });
    return (value: FinanceProgress) => {
      if (!signal.aborted && generation.current === request) setState({ scope, value });
    };
  }, [scope]);
  return { progress: state.scope === scope ? state.value : null, start };
}

export function FinanceLoadingProgress({ progress, scope, forecast = false, refreshing = false }: {
  progress: FinanceProgress | null; scope: string; forecast?: boolean; refreshing?: boolean;
}) {
  const stage = stages[progress?.stage ?? "sources"];
  const steps = forecast ? ["Sources", "Calculation", "Your table"] : ["Sources", "Calculation", "Month detail", "Your table"];
  const step = forecast && stage.step > 1 ? 2 : stage.step;
  const counted = progress?.total !== undefined;
  const label = `${stage.label}${counted ? ` · ${progress.completed} of ${progress.total} months` : "…"}`;
  return <div className="fin-progress" data-testid="finance-loading-progress">
    <div className="fin-progress-heading">
      <span className="fin-progress-icon" aria-hidden="true"><ChartNoAxesColumnIncreasing size={21} strokeWidth={1.6} /></span>
      <div className="fin-progress-copy"><span className="fin-progress-eyebrow">{refreshing ? "Refreshing your report" : "Preparing your report"}<span> / {scope}</span></span>
        <p role="status" aria-live="polite" aria-atomic="true">{label}</p></div>
      {counted && <span className="fin-progress-count" aria-hidden="true"><strong>{progress.completed}</strong><span> / {progress.total} months</span></span>}
    </div>
    <div className="fin-progress-track" role="progressbar" aria-label={stage.label} aria-valuemin={counted ? 0 : undefined}
      aria-valuemax={progress?.total} aria-valuenow={counted ? progress.completed : undefined} aria-valuetext={label}
      data-determinate={counted || undefined} style={counted ? { "--fin-progress-fill": `${progress.completed! / progress.total! * 100}%` } as CSSProperties : undefined}>
      <span />
    </div>
    <ol className="fin-progress-steps" aria-label="Report preparation stages">{steps.map((name, index) => <li key={name} data-state={index < step ? "done" : index === step ? "active" : "pending"} aria-current={index === step ? "step" : undefined}>
      <span className="fin-progress-step-dot" aria-hidden="true">{index < step ? <Check size={10} strokeWidth={2.5} /> : <span />}</span>{name}
    </li>)}</ol>
    {refreshing && <span className="fin-progress-refresh-note">Showing the last loaded report while this update is prepared.</span>}
  </div>;
}
